import React, { useState, useEffect, useRef } from 'react';
import { 
  Canvas,
  IText,
  Rect,
  Circle,
  Triangle,
  Image as FabricImage,
  Point,
  Line,
  util,
  filters
} from 'fabric';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slider,
  Divider
} from '@mui/material';
import { 
  ImagePlus,
  Type,
  Square,
  Circle as CircleIcon,
  Triangle as TriangleIcon,
  Trash2,
  Download,
  Wand2,
  Bold,
  Italic,
  Paintbrush,
  PenTool,
  Maximize,
  Palette,
  ZoomIn,
  ZoomOut,
  HelpCircle,
  Settings,
  Monitor,
  Smartphone,
  Tablet,
  Sliders,
  Image as ImageIcon,
  X,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  Group,
  Ungroup,
  Grid as GridIcon,
  Magnet,
  RulerSquare,
  BoxSelect,
  Gradient,
  Shadow,
} from 'lucide-react';
import PageHeader from './PageHeader';

const HelpDialog = ({ open, onClose }) => (
  <Dialog 
    open={open} 
    onClose={onClose}
    maxWidth="md"
    PaperProps={{ 
      sx: { 
        borderRadius: '12px',
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)'
      } 
    }}
  >
    <DialogTitle sx={{ 
      pb: 1, 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)' 
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HelpCircle size={20} color="#E04D16" />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
          How to Use the Social Media Creator
        </Typography>
      </Box>
      <IconButton onClick={onClose} size="small">
        <X size={18} />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ pt: 3, pb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
            Getting Started
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create stunning social media images in just a few steps:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose a canvas size preset for your target platform
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Add images, text, and shapes using the toolbar
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Customize colors, fonts, and other properties
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
            Canvas Sizes
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Facebook Post:</strong> 1200×630 pixels
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Instagram Post:</strong> 1080×1080 pixels
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Instagram Story:</strong> 1080×1920 pixels
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Twitter Header:</strong> 1500×500 pixels
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
            Tips & Tricks
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Use the alignment tools to create professional-looking designs
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Layer elements by bringing important items to the front
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Export your image in high quality PNG format
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      <Button 
        onClick={onClose}
        variant="contained"
        sx={{
          py: 1,
          px: 4,
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
          '&:hover': { background: 'linear-gradient(45deg, #CC4414, #E85E35)' }
        }}
      >
        Got It
      </Button>
    </DialogActions>
  </Dialog>
);

const TemplatesPanel = ({ onApply }) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="subtitle2" sx={{ mb: 2 }}>Templates</Typography>
    <Grid container spacing={1}>
      {[
        { id: 'social-quote', name: 'Quote Post', preview: '📜' },
        { id: 'promotion', name: 'Promotion', preview: '🎯' },
        { id: 'announcement', name: 'Announcement', preview: '📢' },
        { id: 'testimonial', name: 'Testimonial', preview: '⭐' },
      ].map((template) => (
        <Grid item xs={6} key={template.id}>
          <Paper
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.50' },
            }}
            onClick={() => onApply(template.id)}
          >
            <Typography variant="h4" sx={{ mb: 1 }}>{template.preview}</Typography>
            <Typography variant="caption">{template.name}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const SocialImageCreator = () => {
  // Canvas and refs
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);

  // UI State
  const [activeTab, setActiveTab] = useState(0);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Canvas Settings
  const [canvasSize, setCanvasSize] = useState('1200x630');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');

  // Object Properties
  const [textColor, setTextColor] = useState('#000000');
  const [fontSize, setFontSize] = useState(40);
  const [fontFamily, setFontFamily] = useState('Inter');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(0);
  const [opacity, setOpacity] = useState(1);

  const canvasContainerRef = useRef(null);

  // New states
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(20);
  const [guides, setGuides] = useState({ horizontal: [], vertical: [] });
  const [selectedTemplate, setSelectedTemplate] = useState('blank');
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);

  // Add this state for the warning dialog
  const [sizeChangeDialog, setSizeChangeDialog] = useState({ open: false, newSize: null });

  // Move the templates definition here, after all the state definitions
  const TEMPLATES = {
    'social-quote': {
      name: 'Quote Post',
      setup: (canvas) => {
        // Background
        const rect = new Rect({
          left: canvas.width / 2,
          top: canvas.height / 2,
          width: canvas.width,
          height: canvas.height,
          fill: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
          originX: 'center',
          originY: 'center',
          selectable: false
        });
        
        // Quote text
        const quoteText = new IText('Enter your quote here', {
          left: canvas.width / 2,
          top: canvas.height / 2 - 40,
          fontSize: 36,
          fontFamily: 'Inter',
          fill: '#FFFFFF',
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          fontWeight: 'bold'
        });
        
        // Author text
        const authorText = new IText('- Author Name', {
          left: canvas.width / 2,
          top: canvas.height / 2 + 40,
          fontSize: 24,
          fontFamily: 'Inter',
          fill: '#FFFFFF',
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          fontStyle: 'italic'
        });
        
        canvas.add(rect, quoteText, authorText);
      }
    },
    'promotion': {
      name: 'Promotion',
      setup: (canvas) => {
        // Background
        const rect = new Rect({
          left: canvas.width / 2,
          top: canvas.height / 2,
          width: canvas.width,
          height: canvas.height,
          fill: '#F8FAFC',
          originX: 'center',
          originY: 'center',
          selectable: false
        });
        
        // Heading
        const heading = new IText('SPECIAL OFFER', {
          left: canvas.width / 2,
          top: canvas.height * 0.3,
          fontSize: 48,
          fontFamily: 'Inter',
          fill: '#E04C16',
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          fontWeight: 'bold'
        });
        
        // Description
        const description = new IText('Limited time offer\nDon\'t miss out!', {
          left: canvas.width / 2,
          top: canvas.height * 0.5,
          fontSize: 32,
          fontFamily: 'Inter',
          fill: '#1F2937',
          textAlign: 'center',
          originX: 'center',
          originY: 'center'
        });
        
        canvas.add(rect, heading, description);
      }
    }
  };

  // Remove or comment out any other canvas initialization useEffects
  // Keep only this one main initialization effect:

  useEffect(() => {
    // Clean up any existing canvas instance first
    if (canvasRef.current && canvasRef.current.fabric) {
      canvasRef.current.fabric.dispose();
      canvasRef.current.fabric = null;
    }

    // Initialize new canvas
    const [width, height] = canvasSize.split('x').map(Number);
    const fabricCanvas = new Canvas(canvasRef.current, {
      width,
      height,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      selection: true,
    });

    // Store fabric instance on the canvas element
    canvasRef.current.fabric = fabricCanvas;

    // Add event listeners
    fabricCanvas.on('selection:created', handleObjectSelection);
    fabricCanvas.on('selection:updated', handleObjectSelection);
    fabricCanvas.on('selection:cleared', () => setSelectedObject(null));
    
    // Add history event handlers
    fabricCanvas.on('object:modified', updateHistory);
    fabricCanvas.on('object:added', updateHistory);
    fabricCanvas.on('object:removed', updateHistory);

    // Add snap to grid functionality
    if (snapToGrid) {
      fabricCanvas.on('object:moving', (e) => {
        const obj = e.target;
        obj.set({
          left: Math.round(obj.left / gridSize) * gridSize,
          top: Math.round(obj.top / gridSize) * gridSize
        });
      });
    }

    setCanvas(fabricCanvas);
    
    // Initialize history with empty canvas
    updateHistory();

    // Fit to view after canvas is initialized
    setTimeout(() => {
      fitToView();
    }, 100);

    // Cleanup function
    return () => {
      if (canvasRef.current && canvasRef.current.fabric) {
        canvasRef.current.fabric.dispose();
        canvasRef.current.fabric = null;
        setCanvas(null);
      }
    };
  }, [canvasSize, snapToGrid, gridSize]); // Add dependencies that should trigger canvas reinitialization

  const handleObjectSelection = (e) => {
    const obj = e.selected[0];
    setSelectedObject(obj);
    updateObjectProperties(obj);
  };

  const updateObjectProperties = (obj) => {
    if (!obj) return;

    if (obj instanceof IText) {
      setTextColor(obj.fill || '#000000');
      setFontSize(obj.fontSize || 40);
      setFontFamily(obj.fontFamily || 'Inter');
      setIsBold(obj.fontWeight === 'bold');
      setIsItalic(obj.fontStyle === 'italic');
    }
    
    setStrokeColor(obj.stroke || '#000000');
    setStrokeWidth(obj.strokeWidth || 0);
    setOpacity(obj.opacity || 1);
  };

  // Add text object
  const addText = () => {
    if (!canvas) return;

    const text = new IText('Edit Me', {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fontSize: 40,
      fontFamily: 'Inter',
      fill: textColor,
      textAlign: 'center',
      selectable: true,
      editable: true,
      hasControls: true,
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.requestRenderAll();
  };

  // Add shape objects
  const addShape = (type) => {
    if (!canvas) return;

    let shape;
    const commonProps = {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fill: '#E04D16',
      stroke: '#000000',
      strokeWidth: 0,
      width: 100,
      height: 100,
      selectable: true,
      hasControls: true,
    };

    switch (type) {
      case 'rect':
        shape = new Rect(commonProps);
        break;
      case 'circle':
        shape = new Circle({
          ...commonProps,
          radius: 50,
        });
        break;
      case 'triangle':
        shape = new Triangle(commonProps);
        break;
      default:
        return;
    }

    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.requestRenderAll();
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    if (!canvas || !e.target.files?.[0]) return;

    setLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const imgElement = document.createElement('img');
      imgElement.crossOrigin = 'anonymous';
      imgElement.src = event.target.result;
      
      imgElement.onload = () => {
        try {
          const imgInstance = new FabricImage(imgElement, {
            selectable: true,
            hasControls: true,
            hasBorders: true,
            lockUniScaling: false,
          });

          // Calculate the scale to fit the canvas while maintaining aspect ratio
          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;
          const imgWidth = imgInstance.width;
          const imgHeight = imgInstance.height;

          const scaleX = (canvasWidth * 0.8) / imgWidth;
          const scaleY = (canvasHeight * 0.8) / imgHeight;
          const scale = Math.min(scaleX, scaleY);

          // Center the image on the canvas
          const left = (canvasWidth - imgWidth * scale) / 2;
          const top = (canvasHeight - imgHeight * scale) / 2;

          imgInstance.set({
            scaleX: scale,
            scaleY: scale,
            left: left,
            top: top,
          });

          canvas.add(imgInstance);
          canvas.setActiveObject(imgInstance);
          canvas.requestRenderAll();
          setSelectedObject(imgInstance);
          setLoading(false);
        } catch (error) {
          console.error('Error creating fabric image:', error);
          setError('Failed to process image. Please try another file.');
          setLoading(false);
        }
      };

      imgElement.onerror = () => {
        setError('Failed to load image. Please try another file.');
        setLoading(false);
      };
    };

    reader.onerror = () => {
      setError('Failed to read file. Please try another file.');
      setLoading(false);
    };

    reader.readAsDataURL(e.target.files[0]);
  };

  // Delete selected object
  const deleteSelected = () => {
    if (!canvas) return;
    
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      setSelectedObject(null);
    }
  };

  // Export canvas as image
  const exportImage = () => {
    if (!canvas) return;
    
    const dataUrl = canvas.toDataURL({
      format: 'png',
      quality: 1,
    });
    const link = document.createElement('a');
    link.download = 'social-image.png';
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 3));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.1));
  };

  const fitToView = () => {
    if (!canvas || !canvasContainerRef.current) return;
    
    const container = canvasContainerRef.current;
    const containerWidth = container.clientWidth - 48;
    const containerHeight = container.clientHeight - 48;
    
    const canvasRatio = canvas.width / canvas.height;
    const containerRatio = containerWidth / containerHeight;
    
    let scale;
    if (canvasRatio > containerRatio) {
      scale = containerWidth / canvas.width;
    } else {
      scale = containerHeight / canvas.height;
    }
    
    scale *= 0.9; // Add margin
    setZoomLevel(scale);
  };

  // Update canvas size effect to handle zoom level changes
  useEffect(() => {
    if (!canvas) return;
    
    const zoomHandler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY;
        
        setZoomLevel(prevZoom => {
          if (delta > 0) {
            return Math.max(prevZoom - 0.05, 0.1);
          } else {
            return Math.min(prevZoom + 0.05, 3);
          }
        });
      }
    };
    
    const canvasContainer = canvasContainerRef.current;
    if (canvasContainer) {
      canvasContainer.addEventListener('wheel', zoomHandler, { passive: false });
    }
    
    return () => {
      if (canvasContainer) {
        canvasContainer.removeEventListener('wheel', zoomHandler);
      }
    };
  }, [canvas]);

  // Add this effect to handle pan/drag functionality for the canvas when zoomed
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!canvasContainerRef.current) return;
    
    const container = canvasContainerRef.current;
    
    const handleMouseDown = (e) => {
      // Only enable dragging with middle mouse button or when holding space
      if (e.button === 1 || (e.button === 0 && e.altKey)) {
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        container.style.cursor = 'grabbing';
        e.preventDefault();
      }
    };
    
    const handleMouseMove = (e) => {
      if (isDragging) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        
        setCanvasOffset(prev => ({
          x: prev.x + dx,
          y: prev.y + dy
        }));
        
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    };
    
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        container.style.cursor = 'default';
      }
    };
    
    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Modify the handleSizeChange function to properly handle canvas resizing
  const handleSizeChange = (e) => {
    const newSize = e.target.value;
    setSizeChangeDialog({ open: true, newSize });
  };

  const handleConfirmSizeChange = () => {
    const newSize = sizeChangeDialog.newSize;
    setSizeChangeDialog({ open: false, newSize: null });
    
    if (!canvas || !newSize) return;
    
    const [newWidth, newHeight] = newSize.split('x').map(Number);
    
    // Create a new canvas with the new dimensions
    if (canvasRef.current && canvasRef.current.fabric) {
      canvasRef.current.fabric.dispose();
      canvasRef.current.fabric = null;
    }
    
    const fabricCanvas = new Canvas(canvasRef.current, {
      width: newWidth,
      height: newHeight,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      selection: true,
    });
    
    canvasRef.current.fabric = fabricCanvas;
    
    // Add event listeners
    fabricCanvas.on('selection:created', handleObjectSelection);
    fabricCanvas.on('selection:updated', handleObjectSelection);
    fabricCanvas.on('selection:cleared', () => setSelectedObject(null));
    
    fabricCanvas.on('object:modified', updateHistory);
    fabricCanvas.on('object:added', updateHistory);
    fabricCanvas.on('object:removed', updateHistory);
    
    setCanvas(fabricCanvas);
    setCanvasSize(newSize);
    
    // Reset view
    setCanvasOffset({ x: 0, y: 0 });
    setTimeout(fitToView, 100);
  };

  // Add history functions
  const updateHistory = () => {
    if (!canvas) return;
    
    const newState = JSON.stringify(canvas.toJSON());
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const state = JSON.parse(history[newIndex]);
      canvas.loadFromJSON(state, () => {
        canvas.requestRenderAll();
        setHistoryIndex(newIndex);
      });
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const state = JSON.parse(history[newIndex]);
      canvas.loadFromJSON(state, () => {
        canvas.requestRenderAll();
        setHistoryIndex(newIndex);
      });
    }
  };

  // Add alignment functions
  const alignObjects = (direction) => {
    if (!canvas) return;
    
    const activeSelection = canvas.getActiveObject();
    if (!activeSelection) return;
    
    let objects = activeSelection.type === 'activeSelection' ? 
      activeSelection.getObjects() : [activeSelection];
    
    switch (direction) {
      case 'left':
        const minLeft = canvas.width * -0.5;
        objects.forEach(obj => obj.set('left', minLeft));
        break;
      case 'center':
        objects.forEach(obj => obj.centerH());
        break;
      case 'right':
        const maxRight = canvas.width * 0.5;
        objects.forEach(obj => obj.set('left', maxRight - obj.width * obj.scaleX));
        break;
    }
    
    canvas.requestRenderAll();
    updateHistory();
  };

  // Add grouping functions
  const handleGroupObjects = () => {
    if (!canvas) return;
    
    const activeSelection = canvas.getActiveObject();
    if (!activeSelection || activeSelection.type !== 'activeSelection') return;
    
    const group = activeSelection.toGroup();
    canvas.setActiveObject(group);
    canvas.requestRenderAll();
    updateHistory();
  };

  const handleUngroupObjects = () => {
    if (!canvas) return;
    
    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'group') return;
    
    const items = activeObject.getObjects();
    activeObject.destroy();
    canvas.remove(activeObject);
    
    items.forEach(obj => {
      canvas.add(obj);
    });
    
    canvas.requestRenderAll();
    updateHistory();
  };

  // Add grid functions
  const toggleGrid = () => {
    setShowGrid(!showGrid);
    if (!showGrid) {
      drawGrid();
    } else {
      clearGrid();
    }
  };

  const drawGrid = () => {
    if (!canvas) return;
    
    clearGrid();
    
    const width = canvas.width;
    const height = canvas.height;
    
    for (let i = 0; i < width / gridSize; i++) {
      canvas.add(new Line([i * gridSize, 0, i * gridSize, height], {
        stroke: '#ccc',
        selectable: false,
        evented: false,
        class: 'grid-line'
      }));
    }
    
    for (let i = 0; i < height / gridSize; i++) {
      canvas.add(new Line([0, i * gridSize, width, i * gridSize], {
        stroke: '#ccc',
        selectable: false,
        evented: false,
        class: 'grid-line'
      }));
    }
    
    canvas.requestRenderAll();
  };

  const clearGrid = () => {
    if (!canvas) return;
    
    const gridLines = canvas.getObjects().filter(obj => obj.class === 'grid-line');
    gridLines.forEach(line => canvas.remove(line));
    canvas.requestRenderAll();
  };

  // Add guide functions
  const addGuide = (orientation) => {
    if (!canvas) return;
    
    const guide = new Line(
      orientation === 'horizontal' 
        ? [0, canvas.height / 2, canvas.width, canvas.height / 2]
        : [canvas.width / 2, 0, canvas.width / 2, canvas.height],
      {
        stroke: '#00f',
        strokeWidth: 1,
        selectable: true,
        lockMovementX: orientation === 'horizontal',
        lockMovementY: orientation === 'vertical',
        class: 'guide-line'
      }
    );
    
    canvas.add(guide);
    setGuides(prev => ({
      ...prev,
      [orientation]: [...prev[orientation], guide]
    }));
  };

  // Add these image filter functions
  const applyImageFilters = (obj) => {
    if (!obj || !(obj instanceof FabricImage)) return;
    
    const filterArray = [];
    
    if (brightness !== 0) {
      filterArray.push(new filters.Brightness({
        brightness: brightness / 100
      }));
    }
    
    if (contrast !== 0) {
      filterArray.push(new filters.Contrast({
        contrast: contrast / 100
      }));
    }
    
    if (saturation !== 0) {
      filterArray.push(new filters.Saturation({
        saturation: saturation / 100
      }));
    }
    
    obj.filters = filterArray;
    obj.applyFilters();
    canvas.requestRenderAll();
  };

  // Add the handleApplyTemplate function
  const handleApplyTemplate = (templateId) => {
    if (!canvas) return;
    
    // Clear existing canvas
    canvas.clear();
    
    // Apply template
    const template = TEMPLATES[templateId];
    if (template) {
      template.setup(canvas);
    }
    
    canvas.requestRenderAll();
    updateHistory();
  };

  // Add this dialog component near your other dialogs
  const SizeChangeDialog = ({ open, onClose, onConfirm }) => (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{ 
        sx: { 
          borderRadius: '12px',
          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)'
        } 
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)' 
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
          Change Canvas Size?
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Changing the canvas size will reset the position of all elements. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          variant="contained"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
            '&:hover': { background: 'linear-gradient(45deg, #CC4414, #E85E35)' }
          }}
        >
          Change Size
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PageHeader 
        category="Content Creation"
        title="Social Image Creator" 
        description="Create stunning images for social media posts with our easy-to-use editor"
        icon={<ImageIcon size={24} color="#E04D16" />}
      />

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', px: 3, pb: 3 }}>
        {/* Canvas Area */}
        <Paper 
          elevation={0} 
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Toolbar */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Add Elements */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Add Text">
                <IconButton onClick={addText}>
                  <Type size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Rectangle">
                <IconButton onClick={() => addShape('rect')}>
                  <Square size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Circle">
                <IconButton onClick={() => addShape('circle')}>
                  <CircleIcon size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Triangle">
                <IconButton onClick={() => addShape('triangle')}>
                  <TriangleIcon size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Upload Image">
                <IconButton component="label">
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  <ImagePlus size={20} />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Canvas Size */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={canvasSize}
                onChange={handleSizeChange}
                displayEmpty
              >
                <MenuItem value="1200x630">Facebook Post (1200×630)</MenuItem>
                <MenuItem value="1080x1080">Instagram Post (1080×1080)</MenuItem>
                <MenuItem value="1080x1920">Instagram Story (1080×1920)</MenuItem>
                <MenuItem value="1500x500">Twitter Header (1500×500)</MenuItem>
                <MenuItem value="1200x628">LinkedIn Post (1200×628)</MenuItem>
              </Select>
            </FormControl>

            <Divider orientation="vertical" flexItem />

            {/* Object Properties */}
            {selectedObject && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {selectedObject instanceof IText && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={fontFamily}
                        onChange={(e) => {
                          setFontFamily(e.target.value);
                          selectedObject.set({ fontFamily: e.target.value });
                          canvas.requestRenderAll();
                        }}
                      >
                        <MenuItem value="Inter">Inter</MenuItem>
                        <MenuItem value="Arial">Arial</MenuItem>
                        <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      type="number"
                      size="small"
                      value={fontSize}
                      onChange={(e) => {
                        setFontSize(Number(e.target.value));
                        selectedObject.set({ fontSize: Number(e.target.value) });
                        canvas.requestRenderAll();
                      }}
                      sx={{ width: 80 }}
                    />
                    <IconButton 
                      onClick={() => {
                        setIsBold(!isBold);
                        selectedObject.set({ fontWeight: !isBold ? 'bold' : 'normal' });
                        canvas.requestRenderAll();
                      }}
                    >
                      <Bold size={20} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setIsItalic(!isItalic);
                        selectedObject.set({ fontStyle: !isItalic ? 'italic' : 'normal' });
                        canvas.requestRenderAll();
                      }}
                    >
                      <Italic size={20} />
                    </IconButton>
                  </>
                )}
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => {
                    setTextColor(e.target.value);
                    selectedObject.set({ fill: e.target.value });
                    canvas.requestRenderAll();
                  }}
                  style={{ width: 30, height: 30 }}
                />
                <Tooltip title="Delete">
                  <IconButton onClick={deleteSelected} color="error">
                    <Trash2 size={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Tooltip title="Export">
                <IconButton onClick={exportImage}>
                  <Download size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help">
                <IconButton onClick={() => setHelpDialogOpen(true)}>
                  <HelpCircle size={20} />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Undo">
                <IconButton onClick={handleUndo} disabled={historyIndex <= 0}>
                  <Undo size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Redo">
                <IconButton onClick={handleRedo} disabled={historyIndex >= history.length - 1}>
                  <Redo size={20} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Image filters when an image is selected */}
            {selectedObject instanceof FabricImage && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="caption">Brightness</Typography>
                  <Slider
                    size="small"
                    value={brightness}
                    min={-100}
                    max={100}
                    sx={{ width: 100 }}
                    onChange={(e, value) => {
                      setBrightness(value);
                      applyImageFilters(selectedObject);
                    }}
                  />
                  <Typography variant="caption">Contrast</Typography>
                  <Slider
                    size="small"
                    value={contrast}
                    min={-100}
                    max={100}
                    sx={{ width: 100 }}
                    onChange={(e, value) => {
                      setContrast(value);
                      applyImageFilters(selectedObject);
                    }}
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Canvas */}
          <Box 
            ref={canvasContainerRef}
            sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'grey.100',
              p: 3,
              overflow: 'hidden',
              position: 'relative',
              cursor: isDragging ? 'grabbing' : 'default'
            }}
          >
            {/* Zoom Controls */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                display: 'flex',
                gap: 1,
                bgcolor: 'white',
                borderRadius: '8px',
                padding: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1,
              }}
            >
              <Tooltip title="Zoom Out">
                <IconButton 
                  onClick={zoomOut}
                  size="small"
                  sx={{ 
                    bgcolor: 'white',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <ZoomOut size={18} />
                </IconButton>
              </Tooltip>
              
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  minWidth: '60px',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                }}
              >
                {Math.round(zoomLevel * 100)}%
              </Box>
              
              <Tooltip title="Zoom In">
                <IconButton 
                  onClick={zoomIn}
                  size="small"
                  sx={{ 
                    bgcolor: 'white',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <ZoomIn size={18} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Fit to View">
                <IconButton 
                  onClick={fitToView}
                  size="small"
                  sx={{ 
                    bgcolor: 'white',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <Maximize size={18} />
                </IconButton>
              </Tooltip>
            </Box>

            <div 
              style={{ 
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: 'white',
                transform: `scale(${zoomLevel}) translate(${canvasOffset.x/zoomLevel}px, ${canvasOffset.y/zoomLevel}px)`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
            >
              <canvas ref={canvasRef} />
            </div>
          </Box>
        </Paper>
      </Box>

      {/* Size Change Dialog */}
      <SizeChangeDialog 
        open={sizeChangeDialog.open}
        onClose={() => setSizeChangeDialog({ open: false, newSize: null })}
        onConfirm={handleConfirmSizeChange}
      />
      
      {/* Help Dialog */}
      <HelpDialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </Box>
  );
};

export default SocialImageCreator; 